import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Layout } from "@/components/Layout";
import { RichTextEditor, RichTextDisplay } from "@/components/RichTextEditor";
import { ImageDropZone } from "@/components/ImageDropZone";
import { SlidePanel } from "@/components/SlidePanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Plus, Edit, Trash2, Flag, Search, UserCircle2, Target, Shield, X, Castle, CheckSquare
} from "lucide-react";
import VisibilityToggle from "@/components/VisibilityToggle";
import EntityContextMenu from "@/components/EntityContextMenu";
import { FolderColorStrip } from "@/components/FolderColorStrip";
import { FolderRail, FolderToolbar, FolderCard, partitionCurrentLevel, useFolderCrud } from "@/components/FolderGrid";
import { useMultiSelect, BulkActionBar, SelectCheckbox } from "@/components/MultiSelect";
import { RelatedEntitiesPanel } from "@/components/RelatedEntitiesPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const resolveImg = (val) => (!val ? null : (val.startsWith("http") ? val : `${API}/files/${val}`));

const ALIGNMENTS = [
  "Lawful Good", "Neutral Good", "Chaotic Good",
  "Lawful Neutral", "True Neutral", "Chaotic Neutral",
  "Lawful Evil", "Neutral Evil", "Chaotic Evil",
];

const STATUSES = [
  { value: "active", label: "Active", color: "text-green-400 border-green-400/30" },
  { value: "hidden", label: "Hidden", color: "text-amber-400 border-amber-400/30" },
  { value: "disbanded", label: "Disbanded", color: "text-muted-foreground border-border" },
];

const TITLES = ["Leader", "Second-in-Command", "Lieutenant", "Captain", "Advisor", "Spy", "Enforcer", "Treasurer", "Recruiter", "Member"];

const getStatusConfig = (s) => STATUSES.find((x) => x.value === s) || STATUSES[0];

const FactionsPage = () => {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [factions, setFactions] = useState([]);
  const [npcs, setNpcs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFaction, setEditingFaction] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAlign, setFilterAlign] = useState("all");
  const [selectedFaction, setSelectedFaction] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const confirm = useConfirm();
  const [folders, setFolders] = useState([]);
  const folderCrud = useFolderCrud({ entityType: "factions", onRefresh: () => fetchData() });
  useEffect(() => { setFolders(folderCrud.folders); }, [folderCrud.folders]);
  const multi = useMultiSelect();

  const [form, setForm] = useState({
    name: "", description: "", operations: "", goals: "",
    alignment: "", status: "active", location_id: "", members: [], folder_id: "",
  });

  useEffect(() => { fetchData(); }, [campaignId]);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const fid = searchParams.get("focus");
    if (fid && factions.length) { const f = factions.find(x => x.id === fid); if (f) setSelectedFaction(f); }
  }, [searchParams, factions]);

  const fetchData = async () => {
    try {
      const [campRes, facRes, npcRes, locRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/factions`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/npcs`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/locations`, { withCredentials: true }),
      ]);
      setCampaign(campRes.data);
      setFactions(facRes.data);
      setNpcs(npcRes.data);
      setLocations(locRes.data);
      folderCrud.refresh();
      if (selectedFaction) {
        const updated = facRes.data.find(f => f.id === selectedFaction.id);
        setSelectedFaction(updated || null);
      }
    } catch { toast.error("Failed to load factions"); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({ name: "", description: "", operations: "", goals: "", alignment: "", status: "active", location_id: "", members: [], folder_id: folderCrud.currentFolderId || "" });
    setEditingFaction(null);
    setImageFile(null);
    setImagePreview(null);
  };

  const openCreate = () => { resetForm(); setDialogOpen(true); };

  const openEdit = (faction) => {
    setEditingFaction(faction);
    setForm({
      name: faction.name || "", description: faction.description || "",
      operations: faction.operations || "", goals: faction.goals || "",
      alignment: faction.alignment || "", status: faction.status || "active",
      location_id: faction.location_id || "", members: faction.members || [],
      folder_id: faction.folder_id || "",
    });
    setImageFile(null);
    setImagePreview(null);
    setSelectedFaction(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Faction name is required"); return; }
    try {
      const payload = { ...form, folder_id: form.folder_id || null };
      let faction;
      if (editingFaction) {
        const res = await axios.put(`${API}/factions/${editingFaction.id}`, payload, { withCredentials: true });
        faction = res.data;
        toast.success("Faction updated");
      } else {
        const res = await axios.post(`${API}/campaigns/${campaignId}/factions`, payload, { withCredentials: true });
        faction = res.data;
        toast.success("Faction created");
      }
      if (imageFile && faction?.id) {
        const fd = new FormData();
        fd.append("file", imageFile);
        await axios.post(`${API}/factions/${faction.id}/upload`, fd, { withCredentials: true });
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save faction"); }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete Faction", "This will permanently delete this faction.");
    if (!ok) return;
    try {
      await axios.delete(`${API}/factions/${id}`, { withCredentials: true });
      toast.success("Faction deleted");
      if (selectedFaction?.id === id) setSelectedFaction(null);
      fetchData();
    } catch { toast.error("Failed to delete faction"); }
  };

  const addMember = () => setForm((prev) => ({ ...prev, members: [...prev.members, { npc_id: "", title: "Member" }] }));
  const updateMember = (index, field, value) => setForm((prev) => {
    const members = [...prev.members];
    members[index] = { ...members[index], [field]: value };
    return { ...prev, members };
  });
  const removeMember = (index) => setForm((prev) => ({ ...prev, members: prev.members.filter((_, i) => i !== index) }));

  const getNpcName = (npcId) => npcs.find((n) => n.id === npcId)?.name || "Unknown NPC";
  const getLocationName = (locId) => locations.find((l) => l.id === locId)?.name || null;

  const filtered = factions.filter((f) => {
    const matchSearch = f.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchAlign = filterAlign === "all" || f.alignment === filterAlign;
    return matchSearch && matchAlign;
  });
  const { folders: levelFolders, items: filteredByFolder } = partitionCurrentLevel({
    folders, items: filtered, currentFolderId: folderCrud.currentFolderId
  });
  const searching = searchTerm.trim() !== "" || filterAlign !== "all";
  const displayFolders = searching ? [] : levelFolders;
  const displayItems = searching ? filtered : filteredByFolder;

  const bulkMove = async (folderId) => {
    const ids = [...multi.selectedIds];
    try {
      await Promise.all(ids.map(id => axios.put(`${API}/campaigns/${campaignId}/factions/${id}/folder`, { folder_id: folderId }, { withCredentials: true })));
      setFactions(prev => prev.map(f => ids.includes(f.id) ? { ...f, folder_id: folderId } : f));
      multi.clearAll();
      toast.success(`Moved ${ids.length} factions`);
    } catch { toast.error("Failed to move"); }
  };

  const bulkDelete = async () => {
    const ids = [...multi.selectedIds];
    if (!window.confirm(`Delete ${ids.length} factions?`)) return;
    try {
      await Promise.all(ids.map(id => axios.delete(`${API}/factions/${id}`, { withCredentials: true })));
      setFactions(prev => prev.filter(f => !ids.includes(f.id)));
      multi.clearAll();
      toast.success(`Deleted ${ids.length} factions`);
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) {
    return <Layout campaignName="Loading..."><div className="flex justify-center py-20"><div className="spinner" /></div></Layout>;
  }

  return (
    <Layout campaignName={campaign?.name}>
      <div data-testid="factions-page">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-foreground">Factions</h1>
            <p className="text-muted-foreground text-sm">Organizations, guilds, and secret societies</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant={multi.selectMode ? "default" : "outline"} className="rounded-sm text-xs" onClick={() => multi.setSelectMode(!multi.selectMode)} data-testid="toggle-select-mode">
              <CheckSquare className="w-3.5 h-3.5 mr-1" /> {multi.selectMode ? "Cancel" : "Select"}
            </Button>
            <Button onClick={openCreate} data-testid="create-faction-button" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
              <Plus className="w-4 h-4 mr-2" /> New Faction
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search factions..." className="pl-9" data-testid="faction-search" />
          </div>
          <Select value={filterAlign} onValueChange={setFilterAlign}>
            <SelectTrigger className="w-48" data-testid="faction-filter-alignment"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Alignments</SelectItem>
              {ALIGNMENTS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Folder browser */}
        <div className="flex gap-4 items-start">
          <FolderRail folders={folders} items={factions} currentFolderId={folderCrud.currentFolderId} onNavigate={folderCrud.setCurrentFolderId} allLabel="All Factions" className="w-44 shrink-0 sticky top-2" testIdPrefix="factions" />
          <div className="flex-1 min-w-0">
            <FolderToolbar folders={folders} currentFolderId={folderCrud.currentFolderId} onNavigate={folderCrud.setCurrentFolderId} onNewFolder={folderCrud.createHere} allLabel="All Factions" className="mb-3" />
            {displayFolders.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 mb-4" data-testid="factions-folder-grid">
                {displayFolders.map(f => (
                  <FolderCard key={f.id} folder={f}
                    count={factions.filter(x => x.folder_id === f.id).length + folders.filter(x => x.parent_id === f.id && !x.archived).length}
                    onOpen={() => folderCrud.setCurrentFolderId(f.id)} onRename={() => folderCrud.renameFolder(f)} onDelete={() => folderCrud.deleteFolder(f)} onArchive={() => folderCrud.toggleArchive(f)} onColor={(c) => folderCrud.setFolderColor(f, c)} onDropItem={folderCrud.moveItem} />
                ))}
              </div>
            )}

            {displayItems.length === 0 && displayFolders.length === 0 ? (
              <div className="empty-state">
                <Flag className="empty-state-icon text-muted-foreground w-16 h-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-serif text-foreground mb-2">{factions.length === 0 ? "No factions yet" : "No matches"}</h3>
                <p className="text-muted-foreground mb-6">{factions.length === 0 ? "Organizations, guilds, and secret societies" : "Try a different search"}</p>
                {factions.length === 0 && <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> Create Faction</Button>}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                {displayItems.map((faction) => {
                  const sc = getStatusConfig(faction.status);
                  return (
                    <EntityContextMenu key={faction.id} onOpen={() => setSelectedFaction(faction)} onRename={() => openEdit(faction)} onDelete={() => handleDelete(faction.id)}
                      folders={folders} currentFolderId={faction.folder_id} onMoveFolder={(fid) => folderCrud.moveItem(faction.id, fid)} pinPath="factions" pinLabel={`Faction: ${faction.name}`} entityName={faction.name}>
                      <Card
                        className={`bg-card border-border rounded-sm cursor-pointer entity-card hover:border-primary/40 transition-colors relative ${multi.isSelected(faction.id) ? "ring-2 ring-primary" : ""}`}
                        onClick={() => multi.selectMode ? multi.toggle(faction.id) : setSelectedFaction(faction)}
                        draggable onDragStart={e => e.dataTransfer.setData("entityId", faction.id)}
                        data-testid={`faction-card-${faction.id}`}
                      >
                        <FolderColorStrip entityId={faction.id} folderId={faction.folder_id} folders={folders} onFilter={folderCrud.setCurrentFolderId} testIdPrefix="faction" />
                        {multi.selectMode && <SelectCheckbox checked={multi.isSelected(faction.id)} onChange={() => multi.toggle(faction.id)} />}
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            {faction.image_path ? (
                              <img src={resolveImg(faction.image_path)} alt="" className="w-12 h-12 rounded-sm object-cover flex-shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            ) : (
                              <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0"><Flag className="w-5 h-5 text-primary" /></div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-foreground line-clamp-1">{faction.name}</h3>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <Badge variant="outline" className={`text-[10px] ${sc.color}`}>{sc.label}</Badge>
                                {faction.alignment && <span className="text-[10px] text-muted-foreground">{faction.alignment}</span>}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1.5">
                                {(faction.members || []).length} {(faction.members || []).length === 1 ? "member" : "members"}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </EntityContextMenu>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* SlidePanel Detail */}
        <SlidePanel open={!!selectedFaction} onClose={() => setSelectedFaction(null)} title={selectedFaction?.name || "Faction"}
          items={filtered} currentIndex={selectedFaction ? filtered.findIndex((f) => f.id === selectedFaction.id) : -1}
          onNavigate={(i) => setSelectedFaction(filtered[i])}
        >
          {selectedFaction && (() => {
            const sc = getStatusConfig(selectedFaction.status);
            return (
              <div data-testid="faction-detail-panel">
                {selectedFaction.image_path && (
                  <div className="h-48 w-full rounded-sm overflow-hidden mb-4">
                    <img src={resolveImg(selectedFaction.image_path)} alt={selectedFaction.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  </div>
                )}
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <Badge variant="outline" className={sc.color}>{sc.label}</Badge>
                  {selectedFaction.alignment && <Badge variant="outline" className="border-border text-muted-foreground">{selectedFaction.alignment}</Badge>}
                </div>
                {selectedFaction.location_id && getLocationName(selectedFaction.location_id) && (
                  <div className="flex items-center gap-1.5 text-sm text-foreground/80 mb-4" data-testid="faction-location">
                    <Castle className="w-3.5 h-3.5 text-primary" /> {getLocationName(selectedFaction.location_id)}
                  </div>
                )}
                {selectedFaction.operations && (
                  <div className="mb-4">
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5"><Target className="w-3 h-3" /> Operations</h4>
                    <div className="text-sm text-foreground/80 prose-sm"><RichTextDisplay content={selectedFaction.operations} /></div>
                  </div>
                )}
                {selectedFaction.goals && (
                  <div className="mb-4">
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5"><Shield className="w-3 h-3" /> Goals</h4>
                    <div className="text-sm text-foreground/80 prose-sm"><RichTextDisplay content={selectedFaction.goals} /></div>
                  </div>
                )}
                {selectedFaction.description && (
                  <div className="mb-4">
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Description</h4>
                    <div className="text-sm text-foreground/80 prose-sm"><RichTextDisplay content={selectedFaction.description} /></div>
                  </div>
                )}
                {(selectedFaction.members || []).length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5"><UserCircle2 className="w-3 h-3" /> Members ({selectedFaction.members.length})</h4>
                    <div className="space-y-2">
                      {selectedFaction.members.map((m, i) => (
                        <div key={i} className="flex items-center gap-3 p-2 rounded-sm bg-muted/20 border border-border">
                          <UserCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{getNpcName(m.npc_id)}</p>
                            <p className="text-xs text-primary">{m.title}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="pt-4 border-t border-border mb-4">
                  <RelatedEntitiesPanel entityType="factions" entityId={selectedFaction.id} />
                </div>
                <div className="flex gap-2 pt-4 border-t border-border">
                  <VisibilityToggle entityType="factions" entityId={selectedFaction.id} currentVisibility={selectedFaction.visibility || "dm_only"} onUpdate={(v) => { setSelectedFaction((prev) => ({ ...prev, visibility: v })); fetchData(); }} />
                  <Button variant="outline" size="sm" className="flex-1 rounded-sm" onClick={() => { setSelectedFaction(null); openEdit(selectedFaction); }} data-testid="faction-detail-edit">
                    <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
                  </Button>
                  <Button variant="ghost" size="sm" className="text-destructive rounded-sm" onClick={() => handleDelete(selectedFaction.id)} data-testid="faction-detail-delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })()}
        </SlidePanel>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">{editingFaction ? "Edit Faction" : "New Faction"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Name *</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="The Thieves' Guild" className="bg-input border-border" data-testid="faction-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">Alignment</label>
                  <Select value={form.alignment || "none"} onValueChange={(v) => setForm({ ...form, alignment: v === "none" ? "" : v })}>
                    <SelectTrigger className="bg-input border-border" data-testid="faction-alignment-select"><SelectValue placeholder="Select alignment" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {ALIGNMENTS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">Status</label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="bg-input border-border" data-testid="faction-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">Location</label>
                  <Select value={form.location_id || "none"} onValueChange={(v) => setForm({ ...form, location_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="bg-input border-border" data-testid="faction-location-select"><SelectValue placeholder="Select location..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No location</SelectItem>
                      {locations.map((loc) => <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">Folder</label>
                  <Select value={form.folder_id || "none"} onValueChange={(v) => setForm({ ...form, folder_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="bg-input border-border" data-testid="faction-folder-select"><SelectValue placeholder="Unfiled" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unfiled</SelectItem>
                      {folders.filter(f => !f.archived).sort((a, b) => a.name.localeCompare(b.name)).map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Operations</label>
                <RichTextEditor content={form.operations} onChange={(v) => setForm({ ...form, operations: v })} placeholder="What does this faction do?" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Goals</label>
                <RichTextEditor content={form.goals} onChange={(v) => setForm({ ...form, goals: v })} placeholder="What is this faction working toward?" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Description</label>
                <RichTextEditor content={form.description} onChange={(v) => setForm({ ...form, description: v })} placeholder="Background, history, reputation..." />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Image</label>
                <ImageDropZone imageFile={imageFile} imagePreview={imagePreview} onFile={(f) => { setImageFile(f); setImagePreview(URL.createObjectURL(f)); }} onClear={() => { setImageFile(null); setImagePreview(null); }} existingImagePath={editingFaction?.image_path} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-foreground">NPC Members</label>
                  <Button type="button" variant="outline" size="sm" onClick={addMember} className="border-border" data-testid="add-member-button">
                    <Plus className="w-3 h-3 mr-1" /> Add NPC
                  </Button>
                </div>
                {form.members.length === 0 && <p className="text-sm text-muted-foreground italic">No members added yet.</p>}
                <div className="space-y-2">
                  {form.members.map((member, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`faction-member-row-${i}`}>
                      <Select value={member.npc_id || "none"} onValueChange={(v) => updateMember(i, "npc_id", v === "none" ? "" : v)}>
                        <SelectTrigger className="bg-input border-border flex-1"><SelectValue placeholder="Select NPC..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Select NPC...</SelectItem>
                          {npcs.map((npc) => <SelectItem key={npc.id} value={npc.id}>{npc.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={member.title} onValueChange={(v) => updateMember(i, "title", v)}>
                        <SelectTrigger className="bg-input border-border w-44"><SelectValue /></SelectTrigger>
                        <SelectContent>{TITLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => removeMember(i)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
                <Button onClick={handleSave} className="bg-primary text-primary-foreground" data-testid="save-faction-button">
                  {editingFaction ? "Update Faction" : "Create Faction"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <BulkActionBar count={multi.count} folders={folders} onBulkMove={bulkMove} onBulkDelete={bulkDelete} onClear={multi.clearAll} />
    </Layout>
  );
};

export default FactionsPage;
